/* eslint-disable no-param-reassign */
// Competitive Intel V2 — the report view's Competitive layer data (store key `CompetitiveIntelV2`).
//
// Per V2 report (the Audit's competitiveIntelRequestId):
//   byReport[reportId] = {
//     report,                      // GET reports/{report}: state, message, shown, runInProgress…
//     reportStatus,                // idle | loading | ready | error
//     reportError,                 // { status, message } — 404 = not there / not visible
//     documents: { [`${documentSetId}:${path}`]: { data, status, error } },
//   }
// The Audit wizard's draft state stays in reportCompetitiveIntel.slice.js.

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getDocument, getReport } from './competitiveIntelV2Api';

export const LOAD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
};

export const documentKey = (documentSetId, path) => `${documentSetId}:${path}`;

const errorOf = (error) => ({
  status: error?.status ?? null,
  message: error?.message || 'We couldn’t load this competitive report.',
});

const emptyEntry = () => ({
  report: null,
  reportStatus: LOAD_STATUS.IDLE,
  reportError: null,
  documents: {},
});

const entryOf = (state, reportId) => {
  const key = String(reportId);
  if (!state.byReport[key]) state.byReport[key] = emptyEntry();
  return state.byReport[key];
};

/** GET reports/{report}. `silent` keeps what's on show while a refresh (signal / poll) runs. */
export const fetchCiReport = createAsyncThunk(
  'CompetitiveIntelV2/fetchReport',
  async ({ reportId }, { rejectWithValue }) => {
    try {
      const { data } = await getReport(reportId);
      return data;
    } catch (error) {
      return rejectWithValue(errorOf(error));
    }
  }
);

/** One document of the report on show, e.g. "summary/one-month". */
export const fetchCiDocument = createAsyncThunk(
  'CompetitiveIntelV2/fetchDocument',
  async ({ reportId, documentSetId, path }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getDocument(reportId, documentSetId, path);
      return data;
    } catch (error) {
      // 410: newer content replaced this set. Re-read the report; its new `shown.documentSetId`
      // loads the page again. Nothing to show the member.
      if (error?.status === 410) {
        dispatch(fetchCiReport({ reportId, silent: true }));
        return rejectWithValue({ ...errorOf(error), replaced: true });
      }
      return rejectWithValue(errorOf(error));
    }
  },
  {
    // A document of a published set doesn't change: fetch it once.
    condition: ({ reportId, documentSetId, path }, { getState }) => {
      const entry = getState().CompetitiveIntelV2.byReport[String(reportId)];
      const doc = entry?.documents[documentKey(documentSetId, path)];
      return !doc || doc.status === LOAD_STATUS.ERROR;
    },
  }
);

const CompetitiveIntelV2Slice = createSlice({
  name: 'CompetitiveIntelV2',
  initialState: { byReport: {} },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCiReport.pending, (state, action) => {
        const entry = entryOf(state, action.meta.arg.reportId);
        if (!action.meta.arg.silent || !entry.report) entry.reportStatus = LOAD_STATUS.LOADING;
      })
      .addCase(fetchCiReport.fulfilled, (state, action) => {
        const entry = entryOf(state, action.meta.arg.reportId);
        entry.report = action.payload;
        entry.reportStatus = LOAD_STATUS.READY;
        entry.reportError = null;
      })
      .addCase(fetchCiReport.rejected, (state, action) => {
        const entry = entryOf(state, action.meta.arg.reportId);
        // A failed background refresh keeps the report on show.
        if (action.meta.arg.silent && entry.report) return;
        entry.reportStatus = LOAD_STATUS.ERROR;
        entry.reportError = action.payload || errorOf(action.error);
      })
      .addCase(fetchCiDocument.pending, (state, action) => {
        const { reportId, documentSetId, path } = action.meta.arg;
        entryOf(state, reportId).documents[documentKey(documentSetId, path)] = {
          data: null,
          status: LOAD_STATUS.LOADING,
          error: null,
        };
      })
      .addCase(fetchCiDocument.fulfilled, (state, action) => {
        const { reportId, documentSetId, path } = action.meta.arg;
        entryOf(state, reportId).documents[documentKey(documentSetId, path)] = {
          data: action.payload,
          status: LOAD_STATUS.READY,
          error: null,
        };
      })
      .addCase(fetchCiDocument.rejected, (state, action) => {
        const { reportId, documentSetId, path } = action.meta.arg;
        const { documents } = entryOf(state, reportId);
        if (action.payload?.replaced) {
          // Stays "loading" for the member until the new set's page arrives.
          documents[documentKey(documentSetId, path)] = {
            data: null,
            status: LOAD_STATUS.LOADING,
            error: null,
          };
          return;
        }
        documents[documentKey(documentSetId, path)] = {
          data: null,
          status: LOAD_STATUS.ERROR,
          error: action.payload || errorOf(action.error),
        };
      });
  },
});

export const selectCiReportEntry = (state, reportId) =>
  (reportId != null && state.CompetitiveIntelV2.byReport[String(reportId)]) || null;

export default CompetitiveIntelV2Slice.reducer;
